/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import {
  Amount,
  Application,
  Article,
  CheckoutModel,
  CheckoutModelRequest,
  CheckoutV2Api,
  CodeResponse,
  Concession,
  Contact,
  ContactApi,
  ContactFields,
  ContactFieldsRequest,
  ContactId,
  ContactNode,
  ContactNodeRequest,
  CorporatePassApi,
  CreateContactParams,
  Enrolment,
  FieldSet,
  GetCorporatePassRequest,
  Membership,
  PaymentStatus,
  ProductContainer,
  PromotionApi, V2PaymentPrepareRequest,
  V2PaymentResponse,
  Voucher,
  WaitingList,
} from '@api';
import { AxiosResponse } from 'axios';
import * as L from 'lodash';
import { delay, from, Observable } from 'rxjs';
import { sendAdobeAnalyticsEvent } from 'src/js/actions/AdobeAnalyticActions';
import { ProcessError } from 'src/js/common/epics/EpicUtils';
import { ProcessCheckoutModel } from 'src/js/enrol/epics/EpicProceedToPayment';
import CheckoutApiService from 'src/js/enrol/services/CheckoutApiService';
import { IAction } from '../../actions/IshAction';
import { toServerValues } from '../../components/form/FieldFactory';
import { Injector } from '../../injector';
import { StoreCartContact } from '../../model/checkout/request/StoreCart';
import { CartState, IshState } from '../../services/IshState';
import { changePhase, finishCheckoutProcess } from '../actions/Actions';
import { SubmitContact, Values as ContactValues } from '../containers/contact-add/actions/Actions';
import { getPaymentStatus, resetPaymentState, updatePaymentStatus } from '../containers/payment/actions/Actions';
import { State as PaymentState } from '../containers/payment/reducers/State';
import { CorporatePassFormValues, PaymentService } from '../containers/payment/services/PaymentService';
import { ContactNodeStorage, State } from '../containers/summary/reducers/State';
import { CheckoutState, Phase } from '../reducers/State';

const DELAY_NEXT_PAYMENT_STATUS = 5000;

export class CheckoutService {
  constructor(private contactApi: ContactApi,
    private checkoutApi: CheckoutV2Api,
    private promotionApi: PromotionApi,
    private corporatePassApi: CorporatePassApi) {
  }

  public cartIsEmpty = (cart: CartState): boolean => L.isEmpty(cart.courses.result) && L.isEmpty(cart.products.result) && L.isEmpty(cart.waitingCourses.result);

  public isOnlyWaitingCoursesInCart = (cart: CartState) => cart.waitingCourses.result.length
      && !cart.courses.result.length
      && !cart.products.result.length;

  public isOnlyWaitingCourseSelected = (summary: State) => summary.entities.waitingLists && Object.values<any>(summary.entities.waitingLists || {}).some((e) => e.selected)
      && summary.entities.enrolments && !Object.values<any>(summary.entities.enrolments || {}).some((e) => e.selected)
      && summary.entities.applications && !Object.values<any>(summary.entities.applications || {}).some((e) => e.selected)
      && summary.entities.vouchers && !Object.values<any>(summary.entities.vouchers || {}).some((e) => e.selected)
      && summary.entities.articles && !Object.values<any>(summary.entities.articles || {}).some((e) => e.selected)
      && summary.entities.memberships && !Object.values<any>(summary.entities.memberships || {}).some((e) => e.selected);

  public ifCodeExist = (code, state): boolean => {
    const promotions = state.cart.promotions.entities;
    const inPromotions = Object.keys(promotions).filter((key) => promotions[key].code === code).length;
    const inRedeemVouchers = state.checkout.redeemVouchers.filter((v) => v.key === code).length;
    return !!(inPromotions || inRedeemVouchers);
  };

  public hasPayer = (state: CheckoutState): boolean => !L.isNil(state.payerId);

  public getAllSingleChildIds = (state: CheckoutState): string[] => (
    state.contacts.result.filter((id) => state.contacts.entities.contact && state.contacts.entities.contact[id]?.parentRequired)
  );

  public hasActiveVoucherPayer = (state: CheckoutState): boolean => !!state.redeemVouchers.filter((v) => v.payer).length;

  public hasCartContact = (cart: CartState): boolean => Object.keys(cart.contact).length !== 0;

  public loadFields = (contact: Contact, state: IshState): Promise<ContactFields> => this.contactApi.getContactFields(BuildContactFieldsRequest.fromState(contact, state.cart, state.checkout.newContact, state.checkout.phase));

  public loadFieldsForSelected = (contact: Contact, state: IshState): Promise<ContactFields> => this.contactApi.getContactFields(BuildContactFieldsRequest.fromStateSelected(contact, state.checkout.summary, state.checkout.phase));

  public submitContactDetails = async (values: { [key: string]: any }, fields: ContactFields, contactId): Promise<any> => this.contactApi.submitContactDetails(await BuildSubmitFieldsRequest.fromValues(fields, values, contactId));

  public createOrGetContact = (values: ContactValues, fieldset?: FieldSet): Promise<ContactId> => this.contactApi.createOrGetContact(BuildCreateContactParams.fromValues(values, fieldset));

  public submitCode = (code: string): Promise<CodeResponse> => this.promotionApi.submitCode(code);

  public getConcessionTypes = () => this.contactApi.getConcessionTypes();

  public getContactConcessionsAndMemberships = (payload, state: IshState) => this.contactApi.getContactConcessionsAndMemberships(state.checkout.contacts.result);

  public submitConcession = (payload, props) => this.contactApi.submitConcession(BuildConcessionRequest.fromValues(payload, props));

  public getAmount = async (state: IshState): Promise<Amount> => this.checkoutApi.getCheckoutModel(await BuildCheckoutModelRequest.fromState(state))
    .then((model: CheckoutModel): Promise<Amount> => Promise.resolve(model.amount));

  public getCheckoutModel = async (state: IshState): Promise<CheckoutModel> => this.checkoutApi.getCheckoutModel(await BuildCheckoutModelRequest.fromState(state));

  public getUpdateModel = async (state: IshState, payNow: number): Promise<CheckoutModel> => {
    const request:CheckoutModelRequest = await BuildCheckoutModelRequest.fromState(state);
    request.payNow = payNow;
    return this.checkoutApi.getCheckoutModel(request);
  };

  public submitPaymentCorporatePass = async (values: CorporatePassFormValues, state): Promise<any> => {
    const request: any = await PaymentService.corporatePassValuesToRequest(values, state);
    return this.corporatePassApi.makeCorporatePass(request);
  };

  public isFinalStatus = (value: V2PaymentResponse): boolean => (
    value.status === PaymentStatus.SUCCESSFUL
      || value.status === PaymentStatus.SUCCESSFUL_BY_PASS
      || value.status === PaymentStatus.SUCCESSFUL_WAITING_COURSES
      || value.status === PaymentStatus.UNDEFINED
  );

  public createParentChildrenRelation = (parentId, childIds): Promise<any> => (
    this.contactApi.createParentChildrenRelation({ parentId, childrenIds: childIds })
  );

  public getCorporatePass = (code: string, state: IshState): Promise<any> => (
    this.corporatePassApi.getCorporatePass(BuildGetCorporatePassRequest.fromState(state, code))
  );

  public haveContactSelectedItems = (contact: Contact, summary: State): boolean => {
    const request: ContactFieldsRequest = BuildContactFieldsRequest.fromStateSelected(contact, summary);
    return !!(request.classIds.length || request.waitingCourseIds.length || request.products.length);
  };

  public processPayment = async (payload: any, state: IshState): Promise<any> => {
    const paymentPrepareRequest: V2PaymentPrepareRequest = {
      checkoutModelRequest: await BuildCheckoutModelRequest.fromState(state),
      ccAmount: 0
    };
    return CheckoutApiService.submitPayment(paymentPrepareRequest, state.checkout.payerId);
  }

  public prcessPaymentError = (response: AxiosResponse): IAction<any>[] => {
    if (response && response.data && response.data.payerId && response.data.amount && response.data.contactNodes) {
      return [...ProcessCheckoutModel.process(response.data as CheckoutModel), ...ProcessError(response)];
    } else {
      return [
        changePhase(Phase.Payment),
        resetPaymentState(),
        ...ProcessError(response),
      ];
    }
  };

  public processPaymentResponse = (response: V2PaymentResponse, actions: any[] = []): IAction<any>[] | Observable<any> => {
    switch (response.status) {
      case PaymentStatus.IN_PROGRESS:
        return from([getPaymentStatus()]).pipe(
          delay(DELAY_NEXT_PAYMENT_STATUS)
        );
      case PaymentStatus.SUCCESSFUL:
      case PaymentStatus.SUCCESSFUL_BY_PASS:
      case PaymentStatus.SUCCESSFUL_WAITING_COURSES:
      case PaymentStatus.UNDEFINED:
        return [
          ...actions,
          sendAdobeAnalyticsEvent('purchase', { response }),
          changePhase(Phase.Result),
          updatePaymentStatus(response),
          finishCheckoutProcess(response),
        ];
      case PaymentStatus.FAILED:
        return [
          changePhase(Phase.Result),
          updatePaymentStatus(response),
        ];
      default:
        throw new Error(`Unknown status ${String(response.status)}`);
    }
  };
}

const {
  contactApi,
  promotionApi,
  checkoutApi,
  corporatePassApi,
} = Injector;

const getClassIdsFromSummary = (summary: State, contactId: string): string[] => {
  const classIds = [];
  if (summary.entities.contactNodes && summary.entities.contactNodes[contactId]) {
    summary.entities.contactNodes[contactId].enrolments.forEach((key) => {
      if (summary.entities.enrolments[key] && summary.entities.enrolments[key].classId) {
        classIds.push(summary.entities.enrolments[key].classId);
      }
    });
    summary.entities.contactNodes[contactId].applications.forEach((key) => {
      if (summary.entities.applications[key] && summary.entities.applications[key].classId) {
        classIds.push(summary.entities.applications[key].classId);
      }
    });
  }

  return classIds;
};

const getProductIdsFromCart = (cart: CartState, payerId: string, contactId: string) => cart.products.result.map((productId) => {
  const productType = cart.products.entities[productId] && cart.products.entities[productId].type;

  if (productType === 'VOUCHER' && contactId !== payerId) {
    return null;
  }

  return {
    productId: productId,
    quantity: 1
  }
}).filter((pr) => pr);

export class BuildContactNodeRequest {
  static fromStoredCartContact = (contact: StoreCartContact, promotionIds: string[]): ContactNodeRequest => ({
    contactId: contact.contactId,
    classIds: [...contact?.classes?.map((c) => c.id) || [], ...contact?.applications?.map((a) => a.id) || []],
    waitingCourseIds: contact?.waitingCourses?.map((c) => c.id) || [],
    products: contact.products?.map((p) => ({ productId: p.id, quantity: p.quantity || 1 })) || [],
    promotionIds
  });

  static fromPurchaseItem = (item: any, state: IshState): ContactNodeRequest => {

    const classIds = [];
    const products = [];

    if (item.classId) {
      classIds.push(item.classId);
    }
    if (item.relatedClassId) {
      classIds.push(item.relatedClassId);
    }
    if (item.productId) {
      products.push({ productId: item.productId, quantity: 1 } as ProductContainer);
    }
    if (item.relatedProductId) {
      products.push({ productId: item.relatedProductId, quantity: 1 } as ProductContainer);
    }

    return {
      contactId: item.contactId,
      classIds: classIds,
      products: products,
      promotionIds: state.cart.promotions.result,
      waitingCourseIds: state.cart.waitingCourses.result,
    }
  };

  static fromCartItems = (state: IshState, contactId: string): ContactNodeRequest => {
    const result: ContactNodeRequest = {
      classIds: state.cart.courses.result,
      products: getProductIdsFromCart(state.cart, state.checkout.payerId, contactId),
      contactId: contactId,
      promotionIds: state.checkout.payerId === contactId ? state.cart.promotions.result : [],
      waitingCourseIds: state.cart.waitingCourses.result,
    };
    return Object.keys(result).some((key) => Array.isArray(result[key]) && result[key].length) ? result : null;
  };

  static fromContact = (contact: Contact, summary: State, cart: CartState, payerId: string): ContactNodeRequest => ({
    contactId: contact.id,
    classIds: getClassIdsFromSummary(summary, contact.id),
    products: getProductIdsFromCart(cart, payerId, contact.id),
    promotionIds: cart.promotions.result,
    waitingCourseIds: cart.waitingCourses.result,
  });
}

export class BuildContactFieldsRequest {
  static fromState = (contact: Contact & Pick<SubmitContact, 'updateData'>, cart: CartState, newContact: boolean, phase?: Phase): ContactFieldsRequest => ({
    contactId: contact.id,
    classIds: cart.courses.result,
    products: cart.products.result.map((productId): ProductContainer => ({
      productId: productId,
      quantity: 1
    })),
    waitingCourseIds: cart.waitingCourses.result,
    fieldSet: FieldSet.ENROLMENT,
    mandatoryOnly: typeof contact.updateData === 'boolean' ? !contact.updateData : !newContact,
    isPayer: phase && Phase[phase] === 'AddContactAsPayer',
    isParent: phase && Phase[phase] === 'AddParent',
  });

  static fromStateSelected = (contact: Contact, summary: State, phase?: Phase): ContactFieldsRequest => {
    const result: ContactFieldsRequest = {
      contactId: contact.id,
      classIds: [],
      products: [],
      waitingCourseIds: [],
      isPayer: phase && Phase[phase] === 'AddContactAsPayer',
      isParent: phase && Phase[phase] === 'AddParent',
      fieldSet: FieldSet.ENROLMENT,
      mandatoryOnly: true
    }

    const enrolments = ['enrolments', 'applications'];
    const products = ['vouchers', 'memberships', 'articles'];
    const waitingLists = ['waitingLists'];

    enrolments.map((item) => Object.values<any>(summary.entities[item] || {})
      .filter((e) => e.contactId === contact.id && e.selected)
      .map((e) => result.classIds.push(e.classId)));

    products.map((item) => Object.values<any>(summary.entities[item] || {})
      .filter((e) => e.contactId === contact.id && e.selected)
      .map((e) => {
        const productContainer: ProductContainer = {
          productId: e.productId,
          quantity: e.quantity
        }
        result.products = [productContainer];
      }));

    waitingLists.map((item) => Object.values<any>(summary.entities[item] || {})
      .filter((e) => e.contactId === contact.id && e.selected)
      .map((e) => result.waitingCourseIds.push(e.courseId)));

    return result;
  };
}

export class BuildConcessionRequest {
  static fromValues = (values, state): Concession => ({
    contactId: state.contact.id,
    concessionTypeId: values.concessionType.key,
    expiryDate: values.expiryDate || null,
    number: values.number || null,
  });
}

export class BuildSubmitFieldsRequest {
  static fromValues = async (fields: ContactFields, values: { [key: string]: any }, contactId): Promise<ContactFields>  => {
    for (const fh of fields.headings) {
      await toServerValues(fh.fields, values);
    }
    return {
      contactId: contactId,
      headings: fields.headings
    }
  };
}

export class BuildCreateContactParams {
  static fromValues = (values: ContactValues, fieldset: FieldSet): CreateContactParams => ({
    firstName: values.firstName?.trim(),
    lastName: values.lastName?.trim(),
    email: values.email?.trim(),
    fieldSet: fieldset || FieldSet.ENROLMENT,
    company: values.company || false,
  });
}

const formatNodeCustomFields = async (items: (Enrolment & WaitingList & Application & Membership & Article & Voucher)[], stateRoot: IshState) => {
  for (const it of items) {
    if (it && it.fieldHeadings) {
      for (const fh of it.fieldHeadings) {
        const itemId =  it.classId || it.courseId || it.productId;
        
        const form = stateRoot.form[`${it.contactId}-${itemId}`]

        if (form && form.values) {
          await toServerValues(fh.fields, form.values);
        }
      }
    }
  }
};

export class BuildContactNodes {
  static fromState = async (state: State, stateRoot: IshState): Promise<ContactNode[]> => {
    const nodes = []

    for (const contactId of state.result) {
      const node = await BuildContactNodes.contactNodeBy(state.entities.contactNodes[contactId], state, stateRoot);
      nodes.push(node);
    }

    return nodes
  };

  private static contactNodeBy = async (storage: ContactNodeStorage, state: State, stateRoot: IshState): Promise<ContactNode> => {
    const result: ContactNode = new ContactNode();
    result.contactId = storage.contactId;
    result.enrolments = storage.enrolments ? storage.enrolments.map((id) => L.cloneDeep(state.entities.enrolments[id])) : [];
    await formatNodeCustomFields(result.enrolments as any, stateRoot);
    result.applications = storage.applications ? storage.applications.map((id) => L.cloneDeep(state.entities.applications[id])) : [];
    await formatNodeCustomFields(result.applications as any, stateRoot);
    result.memberships = storage.memberships ? storage.memberships.map((id) => L.cloneDeep(state.entities.memberships[id])) : [];
    await formatNodeCustomFields(result.memberships as any, stateRoot);
    result.articles = storage.articles ? storage.articles.map((id) => L.cloneDeep(state.entities.articles[id])) : [];
    await formatNodeCustomFields(result.articles as any, stateRoot);

    // Make sure that only payer has vouchers
    result.vouchers = state.entities.vouchers && stateRoot.checkout.payerId === storage.contactId
      ? storage.vouchers ? storage.vouchers.map((id) => L.cloneDeep(state.entities.vouchers[id])) : []
      : [];

    await formatNodeCustomFields(result.vouchers as any, stateRoot);

    result.waitingLists = storage.waitingLists ? storage.waitingLists.map((id) => L.cloneDeep(state.entities.waitingLists[id])) : [];
    await formatNodeCustomFields(result.waitingLists as any, stateRoot);
    return result;
  };
}

export class BuildCheckoutModelRequest {
  static fromState = async (state: IshState): Promise<CheckoutModelRequest> => ({
    payerId: state.checkout.payerId,
    promotionIds: state.cart.promotions.result,
    redeemedVoucherIds: state.checkout.redeemVouchers.filter((v) => v.enabled).map((v) => v.id),
    redeemedVoucherProductIds: state.checkout.redeemedVoucherProducts.filter((v) => v.enabled).map((v) => v.id),
    contactNodes: await BuildContactNodes.fromState(state.checkout.summary, state),
    corporatePassId: (state.checkout.payment.corporatePass && state.checkout.payment.corporatePass.id) || null,
    payNow: (state.checkout.amount && state.checkout.amount.isEditable && state.checkout.amount.payNow !== state.checkout.amount.minPayNow) ? state.checkout.amount.payNow : null,
  });
}

export class BuildGetCorporatePassRequest {
  static fromState = (state: IshState, code: string): GetCorporatePassRequest => {
    const result: GetCorporatePassRequest = new GetCorporatePassRequest();
    const entities = state.checkout.summary?.entities || {} as any;
    const products = ['memberships', 'vouchers', 'articles'];
    const productIds = [];

    const classIds = Array.from(new Set(Object.keys(entities.enrolments || {})
      .filter((key) => entities.enrolments[key].selected)
      .map((key) => entities.enrolments[key].classId)));

    products.map((p) => Object.keys(entities[p] || {})
      .filter((key) => entities[p][key].selected)
      .map((key) => productIds.push(entities[p][key].productId)));

    result.classIds = classIds;
    result.productIds = productIds;
    result.code = code;

    return result;
  };
}

export class BuildWaitingCoursesResult {
  static fromState = (state: IshState): any => {
    const result = [];
    const nodes = Object.values<any>(state.checkout.summary.entities.contactNodes || {})
      .map((item) => ({
        contactId: item.contactId,
        coursesIds: item.waitingLists,
      }));

    const filteredNodes = nodes
      .map((node) => ({
        name: `${state.checkout.contacts.entities.contact[node.contactId].firstName} ${state.checkout.contacts.entities.contact[node.contactId].lastName}`,
        courses: node.coursesIds.map((id) => state.checkout.summary.entities.waitingLists[id]).filter((c) => c.selected),
      }));

    filteredNodes.map((n) => result.push({
      name: n.name,
      courses: n.courses.map((c) => ({
        name: state.cart.waitingCourses.entities[c.courseId].name,
      })),
    }));

    return result;
  };
}

export default new CheckoutService(contactApi, checkoutApi, promotionApi, corporatePassApi);
